/* W7.4 — Deal 360 : fiche en LECTURE COMPOSÉE (/app/deals/:id).
 * Compose identity / state / intelligence servies par GET /workspace/deals/{id}/360.
 * Aucune écriture métier depuis cette fiche (W7.4 read-only). */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { DealDecisions } from "@/components/DealDecisions";
import { ObligationsPanel } from "@/components/ObligationsPanel";
import { ExportButton } from "@/components/ExportButton";
import {
  ArrowLeft, Handshake, Briefcase, Building2, Users, FileText, Scale, Brain,
  Clock, AlertTriangle, ChevronRight, Loader2, Sparkles, MapPin, Layers,
  CircleDot, ExternalLink, Zap, X, ClipboardCheck,
} from "lucide-react";

const fmtCHF = (v) =>
  new Intl.NumberFormat("fr-CH", { style: "currency", currency: "CHF", maximumFractionDigits: 0 }).format(v || 0);
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-CH", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "—"; }
};
const scoreColor = (s) => (s >= 70 ? "#1D9E75" : s >= 45 ? "#EF9F27" : "#E24B4A");

const PHASE_LABELS = { discover: "Discover", qualify: "Qualify", structure: "Structure", execute: "Execute", close: "Close" };
const SIGNAL_COLOR = { high: "#E24B4A", medium: "#EF9F27", low: "#71717a" };
const EVENT_ICONS = { lead: Users, opportunity: CircleDot, mandate: Briefcase, deal: Handshake, score: Sparkles, negotiation: Scale, document: FileText };

function EmptyState({ text, testid }) {
  return (
    <div className="p-8 text-center text-xs text-zinc-600 border border-dashed border-white/10 rounded-lg" data-testid={testid}>
      {text}
    </div>
  );
}

function PhaseStepper({ phase, phases, statut, history, terminal, onTransition, busy }) {
  const idx = phase === "closed" ? phases.length - 1 : phases.indexOf(phase);
  const [showHistory, setShowHistory] = useState(false);
  const fmtD = (iso) => { try { return new Date(iso).toLocaleDateString("fr-CH", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1 flex-wrap" data-testid="deal-phase-stepper">
        {phases.map((p, i) => (
          <div key={p} className="flex items-center gap-1">
            <div className={`text-[10px] px-2.5 py-1 rounded-full border transition-colors ${
              i === idx ? "border-[#EFD127]/60 text-[#EFD127] bg-[#EFD127]/10 font-semibold"
                : i < idx ? "border-[#1D9E75]/30 text-[#1D9E75]/70"
                : "border-white/10 text-zinc-600"}`}>
              {PHASE_LABELS[p]}
            </div>
            {i < phases.length - 1 && <ChevronRight className="w-3 h-3 text-zinc-700" />}
          </div>
        ))}
        {terminal && <span className="text-[10px] text-zinc-500 ml-2">statut : {statut}</span>}
        {(history || []).length > 0 && (
          <button onClick={() => setShowHistory(!showHistory)} className="text-[10px] text-zinc-500 hover:text-zinc-300 underline decoration-dotted ml-2"
            data-testid="deal360-phase-history-toggle">
            historique ({history.length})
          </button>
        )}
      </div>
      {!terminal && onTransition && (
        <div className="flex items-center gap-2" data-testid="deal360-phase-actions">
          {idx > 0 && (
            <button disabled={busy} onClick={() => onTransition(phases[idx - 1])}
              className="btn-ghost text-[10px]" data-testid="deal360-phase-back">
              <ArrowLeft className="w-3 h-3" /> Revenir à {PHASE_LABELS[phases[idx - 1]]}
            </button>
          )}
          {idx < phases.length - 1 && (
            <button disabled={busy} onClick={() => onTransition(phases[idx + 1])}
              className="btn-primary text-[10px]" data-testid="deal360-phase-advance">
              Avancer vers {PHASE_LABELS[phases[idx + 1]]} <ChevronRight className="w-3 h-3" />
            </button>
          )}
        </div>
      )}
      {showHistory && (
        <div className="noam-card p-3 space-y-1" data-testid="deal360-phase-history">
          {[...history].reverse().map((h, i) => (
            <div key={i} className="text-[10px] text-zinc-400 flex items-center gap-2">
              <span className="font-mono">{PHASE_LABELS[h.from] || h.from} → {PHASE_LABELS[h.to] || h.to}</span>
              <span className="text-zinc-600">{fmtD(h.at)}</span>
              <span className="text-zinc-600 truncate">{h.by_email}</span>
              {h.reason && <span className="text-zinc-500 italic truncate">« {h.reason} »</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ScoreDonut({ score }) {
  const done = score?.status === "done";
  const g = done ? score.global : null;
  return (
    <div className="flex items-center gap-3">
      <div className="w-14 h-14 shrink-0 rounded-full flex items-center justify-center"
        style={done ? { background: `conic-gradient(${scoreColor(g)} ${g * 3.6}deg, rgba(255,255,255,0.06) 0deg)` } : { background: "rgba(255,255,255,0.04)" }}>
        <div className="w-11 h-11 rounded-full bg-[#0D131B] flex items-center justify-center text-sm font-bold font-mono"
          style={{ color: done ? scoreColor(g) : "#52525b" }} data-testid="deal360-score-value">
          {done ? g : score?.status === "running" ? <Loader2 className="w-4 h-4 animate-spin" /> : "—"}
        </div>
      </div>
      <div className="text-[11px] text-zinc-500">
        <div className="text-zinc-300 font-medium">Score de réalisabilité</div>
        {done ? (score.matchmaking_ready ? <span className="text-[#1D9E75]">Prêt au matchmaking</span> : <span className="text-[#EF9F27]">À repositionner</span>)
          : "Pas encore calculé"}
      </div>
    </div>
  );
}

function Pulse({ intel }) {
  const signals = intel?.signals || [];
  const actions = intel?.next_actions || [];
  return (
    <div className="space-y-4">
      <div className="noam-card p-4" data-testid="deal360-next-actions">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5 mb-2">
          <Zap className="w-3.5 h-3.5 text-[#EFD127]" /> Prochaines actions
        </div>
        {actions.length === 0 && <div className="text-xs text-zinc-600">Rien à signaler — le dossier est en ordre.</div>}
        <div className="space-y-1.5">
          {actions.map((a, i) => (
            <div key={i} className="text-xs text-zinc-300 flex items-start gap-2 p-2 rounded bg-white/[0.03]" data-testid={`deal-next-action-${i}`}>
              <ChevronRight className="w-3.5 h-3.5 text-[#EFD127] shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div>{a.label}</div>
                {a.reason && (
                  <div className="text-[10px] text-zinc-500 mt-0.5" title={a.evidence ? `${a.evidence.source} = ${a.evidence.value}` : ""}>
                    {a.reason}{a.evidence ? ` · ${a.evidence.source}` : ""}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="noam-card p-4" data-testid="deal360-signals">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5 mb-2">
          <AlertTriangle className="w-3.5 h-3.5 text-[#EF9F27]" /> Signaux & blocages
        </div>
        {signals.length === 0 && <div className="text-xs text-zinc-600">Aucun signal détecté.</div>}
        <div className="space-y-1.5">
          {signals.map((s, i) => (
            <div key={i} className="text-xs flex items-start gap-2" data-testid={`deal-signal-${i}`}>
              <span className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5" style={{ background: SIGNAL_COLOR[s.level] }} />
              <span className="text-zinc-400">{s.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function OverviewTab({ data }) {
  const { identity, state } = data;
  const d = identity.deal;
  const prov = identity.provenance;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[["Valeur", fmtCHF(d.valeur_chf)], ["Contrepartie", d.counterparty || "—"],
          ["Créé le", fmtDate(d.created_at)], ["Dernière activité", fmtDate(d.updated_at)]].map(([l, v]) => (
          <div key={l} className="noam-card p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{l}</div>
            <div className="text-sm font-medium mt-1 truncate" title={String(v)}>{v}</div>
          </div>
        ))}
      </div>
      <div className="noam-card p-4" data-testid="deal360-provenance">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Origine du deal</div>
        <div className="flex items-center gap-2 flex-wrap text-xs">
          {prov.chain.length === 0 && <span className="text-zinc-400">Création directe au registre.</span>}
          {prov.chain.map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="px-2 py-1 rounded bg-white/[0.04] border border-white/10 text-zinc-300">
                {c.kind === "lead" && <>Lead · {c.prenom} {c.nom}</>}
                {c.kind === "opportunity" && <>Opportunité · {c.titre} ({c.etape})</>}
                {c.kind === "mandate" && <>Mandat · {c.title}</>}
              </span>
              {i < prov.chain.length - 1 && <ChevronRight className="w-3 h-3 text-zinc-600" />}
            </div>
          ))}
          <ChevronRight className="w-3 h-3 text-zinc-600" />
          <span className="px-2 py-1 rounded border border-[#EFD127]/40 text-[#EFD127] bg-[#EFD127]/10">Deal maître</span>
        </div>
      </div>
      {state.mandate && (
        <div className="noam-card p-4" data-testid="deal360-mandate">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
            <Briefcase className="w-3.5 h-3.5 text-[#EFD127]" /> Mandat d'exécution (Courtage)
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
            <div><span className="text-zinc-500">Client</span><div className="text-zinc-200 mt-0.5">{state.mandate.client || "—"}</div></div>
            <div><span className="text-zinc-500">Statut mandat</span><div className="text-zinc-200 mt-0.5">{state.mandate.statut || "—"}</div></div>
            <div><span className="text-zinc-500">Commission</span><div className="text-zinc-200 mt-0.5">{state.mandate.commission_pct != null ? `${state.mandate.commission_pct}%` : "—"}</div></div>
            <div><span className="text-zinc-500">Closing estimé</span><div className="text-zinc-200 mt-0.5">{fmtDate(state.mandate.closing_estime_at)}</div></div>
          </div>
          <Link to="/app/courtage" className="text-[10px] text-[#EFD127] hover:underline flex items-center gap-1 mt-2 w-fit" data-testid="deal360-mandate-link">
            Ouvrir dans Courtage <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      )}
      {d.notes && (
        <div className="noam-card p-4"><div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Notes</div>
          <p className="text-xs text-zinc-300 whitespace-pre-wrap">{d.notes}</p></div>
      )}
    </div>
  );
}

function AssetTab({ identity }) {
  const a = identity.asset;
  if (!a) {
    return <EmptyState testid="deal360-asset-empty"
      text={`Aucun actif canonique relié à ce deal pour l'instant. Classe d'actif déclarée : ${identity.asset_label}.`} />;
  }
  return (
    <div className="noam-card p-4" data-testid="deal360-asset">
      <div className="flex items-center gap-2 mb-3">
        <Building2 className="w-4 h-4 text-[#378ADD]" />
        <div className="text-sm font-medium">{a.label}</div>
        {identity.canonical?.human_validated && (
          <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-[#1D9E75]/40 text-[#1D9E75]">identité canonique validée</span>
        )}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
        {[["Type", a.type_bien], ["Localisation", a.localisation], ["Surface", a.surface_m2 ? `${a.surface_m2} m²` : null],
          ["Lots", a.nb_lots], ["Stade", a.stade], ["Statut", a.status]].filter(([, v]) => v != null).map(([l, v]) => (
          <div key={l}><span className="text-zinc-500">{l}</span><div className="text-zinc-200 mt-0.5 flex items-center gap-1">
            {l === "Localisation" && <MapPin className="w-3 h-3 text-zinc-500" />}{v}</div></div>
        ))}
      </div>
    </div>
  );
}

const PARTY_ROLES = ["vendeur", "acheteur", "investisseur", "preteur", "conseil", "intermediaire", "autre"];
const DOC_ROLES = ["nda", "teaser", "offre", "contrat", "due_diligence", "financement", "autre"];
const ROLE_LABELS = { vendeur: "Vendeur", acheteur: "Acheteur", investisseur: "Investisseur", preteur: "Prêteur", conseil: "Conseil", intermediaire: "Intermédiaire", autre: "Autre", nda: "NDA", teaser: "Teaser", offre: "Offre", contrat: "Contrat", due_diligence: "Due diligence", financement: "Financement" };

function PartiesTab({ identity, dealId, onReload }) {
  const parties = identity.parties || [];
  const cp = identity.deal.counterparty;
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [role, setRole] = useState("autre");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!adding || q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/workspace/annuaire/entities?search=${encodeURIComponent(q.trim())}`)
        .then((r) => setResults((r.data.items || r.data || []).slice(0, 6)))
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [q, adding]);

  const attach = async (ent) => {
    setBusy(true);
    try {
      const r = await api.post(`/workspace/deals/${dealId}/parties`, { entity_id: ent.id, role });
      if (r.data.already) notify.info("Cette entité est déjà rattachée.");
      else notify.success(`${ent.name} rattaché(e) comme ${ROLE_LABELS[role]}.`);
      setAdding(false); setQ(""); await onReload();
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };
  const requalify = async (p, newRole) => {
    try {
      await api.patch(`/workspace/deals/${dealId}/parties/${p.id}`, { role: newRole });
      notify.success(`${p.name} → ${ROLE_LABELS[newRole]}.`); await onReload();
    } catch (e) { notify.error(fmtError(e)); }
  };
  const remove = async (p) => {
    try {
      await api.delete(`/workspace/deals/${dealId}/parties/${p.id}`);
      notify.success(`${p.name} retiré(e).`); await onReload();
    } catch (e) { notify.error(fmtError(e)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button onClick={() => setAdding(!adding)} className="btn-primary text-xs" data-testid="deal360-add-party">
          <Users className="w-3.5 h-3.5" /> Ajouter une partie
        </button>
      </div>
      {adding && (
        <div className="noam-card p-3 space-y-2" data-testid="deal360-party-form">
          <div className="flex gap-2 flex-wrap">
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher dans l'annuaire (organisation ou personne)…"
              className="noam-input text-xs flex-1 min-w-[220px]" data-testid="deal360-party-search" />
            <select value={role} onChange={(e) => setRole(e.target.value)} className="noam-input text-xs w-40" data-testid="deal360-party-role">
              {PARTY_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select>
          </div>
          {results.map((ent) => (
            <button key={ent.id} disabled={busy} onClick={() => attach(ent)}
              className="w-full text-left p-2 rounded bg-white/[0.03] hover:bg-white/[0.07] text-xs flex items-center gap-2 transition-colors"
              data-testid={`deal360-party-result-${ent.id}`}>
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full border shrink-0 ${ent.type === "person" ? "border-[#378ADD]/40 text-[#378ADD]" : "border-[#1D9E75]/40 text-[#1D9E75]"}`}>
                {ent.type === "person" ? "Personne" : "Org"}
              </span>
              <span className="text-zinc-200 truncate">{ent.name}</span>
              <span className="text-[10px] text-zinc-500 truncate">{ent.sector || ""}</span>
            </button>
          ))}
          {q.trim().length >= 2 && results.length === 0 && (
            <div className="text-[10px] text-zinc-600 p-2">Aucune entité trouvée — l'ajout se fait uniquement depuis l'annuaire (pas de création ici).</div>
          )}
        </div>
      )}
      {cp && (
        <div className="noam-card p-3 text-xs flex items-center gap-2" data-testid="deal360-counterparty">
          <Users className="w-3.5 h-3.5 text-zinc-500" />
          <span className="text-zinc-500">Contrepartie déclarée :</span><span className="text-zinc-200">{cp}</span>
        </div>
      )}
      {parties.length === 0 && !cp && !adding && (
        <EmptyState testid="deal360-parties-empty" text="Aucune organisation ou personne reliée à ce deal. Utilisez « Ajouter une partie »." />
      )}
      {parties.length > 0 && (
        <div className="noam-card divide-y divide-white/5" data-testid="deal360-parties-list">
          {parties.map((p) => (
            <div key={p.id} className="p-3 flex items-center gap-3 text-xs">
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full border shrink-0 ${p.type === "person" ? "border-[#378ADD]/40 text-[#378ADD]" : "border-[#1D9E75]/40 text-[#1D9E75]"}`}>
                {p.type === "person" ? "Personne" : "Organisation"}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-zinc-200 truncate">{p.name}</div>
                <div className="text-[10px] text-zinc-500">{[p.sector, p.country].filter(Boolean).join(" · ") || "—"}</div>
              </div>
              {p.linked_via === "deal" ? (
                <>
                  <select value={p.role || "autre"} onChange={(e) => requalify(p, e.target.value)}
                    className="noam-input text-[10px] py-1 w-32" data-testid={`deal360-party-role-${p.id}`}>
                    {PARTY_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                  </select>
                  <button onClick={() => remove(p)} className="btn-ghost text-[10px] p-1 text-[#E24B4A]"
                    title="Retirer" data-testid={`deal360-party-remove-${p.id}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </>
              ) : (
                <span className="text-[10px] text-zinc-500 shrink-0">via {p.linked_via}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TimelineTab({ timeline }) {
  if (!timeline?.length) return <EmptyState testid="deal360-timeline-empty" text="Aucun événement reconstructible pour ce deal." />;
  return (
    <div className="noam-card p-4" data-testid="deal360-timeline">
      <div className="space-y-0">
        {timeline.map((e, i) => {
          const Icon = EVENT_ICONS[e.type] || Clock;
          return (
            <div key={i} className="flex gap-3 pb-4 relative">
              {i < timeline.length - 1 && <div className="absolute left-[9px] top-6 bottom-0 w-px bg-white/10" />}
              <div className="w-5 h-5 rounded-full bg-white/[0.06] border border-white/10 flex items-center justify-center shrink-0 z-10">
                <Icon className="w-2.5 h-2.5 text-zinc-400" />
              </div>
              <div className="min-w-0 text-xs">
                <div className="text-zinc-300">{e.label}</div>
                <div className="text-[10px] text-zinc-600 mt-0.5">{fmtDate(e.at)}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NegotiationTab({ state, dealId, onReload }) {
  const negs = state.negotiations || [];
  const [opening, setOpening] = useState(false);
  const [title, setTitle] = useState("");
  const [objectif, setObjectif] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!title.trim()) { notify.error("Titre requis."); return; }
    setBusy(true);
    try {
      await api.post(`/workspace/deals/${dealId}/negotiations`, { title: title.trim(), objectif });
      notify.success("Table de négociation ouverte, liée au deal.");
      setOpening(false); setTitle(""); setObjectif(""); await onReload();
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button onClick={() => setOpening(!opening)} className="btn-primary text-xs" data-testid="deal360-open-nego">
          <Scale className="w-3.5 h-3.5" /> Ouvrir une négociation
        </button>
      </div>
      {opening && (
        <div className="noam-card p-3 space-y-2" data-testid="deal360-nego-form">
          <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Titre de la négociation…" className="noam-input text-xs w-full" data-testid="deal360-nego-title" />
          <input value={objectif} onChange={(e) => setObjectif(e.target.value)}
            placeholder="Objectif (optionnel)…" className="noam-input text-xs w-full" data-testid="deal360-nego-objectif" />
          <div className="flex justify-end">
            <button onClick={create} disabled={busy} className="btn-primary text-xs" data-testid="deal360-nego-create">
              {busy ? "Création…" : "Créer et lier au deal"}
            </button>
          </div>
        </div>
      )}
      {negs.length === 0 && !opening ? (
        <EmptyState testid="deal360-nego-empty" text="Aucune table de négociation liée à ce deal. Ouvrez-en une directement ici." />
      ) : negs.length > 0 && (
        <div className="noam-card divide-y divide-white/5" data-testid="deal360-nego-list">
          {negs.map((n) => (
            <Link key={n.id} to="/app/negociation" className="p-3 flex items-center gap-3 text-xs hover:bg-white/[0.03] transition-colors">
              <Scale className="w-3.5 h-3.5 text-[#378ADD] shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-zinc-200 truncate">{n.title || "Négociation"}</div>
                <div className="text-[10px] text-zinc-500">
                  {(n.parties || []).join(" · ") || "parties non renseignées"} · {n.components_count} composante(s)
                  {n.values_compat && " · compatibilité analysée"}
                </div>
              </div>
              <span className="text-[10px] text-zinc-600 shrink-0">{fmtDate(n.created_at)}</span>
              <ExternalLink className="w-3 h-3 text-zinc-600 shrink-0" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function DocumentsTab({ state, dealId, onReload }) {
  const docs = state.documents || [];
  const [docRole, setDocRole] = useState("autre");
  const [uploading, setUploading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [candidates, setCandidates] = useState([]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("module_source", "deal");
      fd.append("linked_module", "deal");
      fd.append("linked_entity_id", dealId);
      const r = await api.post("/workspace/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (docRole !== "autre") {
        await api.post(`/workspace/deals/${dealId}/documents/link`, { document_id: r.data.id, doc_role: docRole });
      }
      notify.success(`« ${file.name} » téléversé et rattaché au deal.`);
      await onReload();
    } catch (e) { notify.error(fmtError(e)); }
    setUploading(false);
  };

  const openLinking = async () => {
    setLinking(!linking);
    if (!linking) {
      try {
        const r = await api.get("/workspace/documents");
        const linkedIds = new Set(docs.map((d) => d.id));
        setCandidates((r.data.items || r.data || []).filter((d) => !linkedIds.has(d.id)).slice(0, 12));
      } catch (e) { notify.error(fmtError(e)); }
    }
  };
  const linkExisting = async (doc) => {
    try {
      await api.post(`/workspace/deals/${dealId}/documents/link`, { document_id: doc.id, doc_role: docRole });
      notify.success(`« ${doc.name} » rattaché au deal.`);
      setLinking(false); await onReload();
    } catch (e) { notify.error(fmtError(e)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 justify-end flex-wrap">
        <select value={docRole} onChange={(e) => setDocRole(e.target.value)} className="noam-input text-xs w-36" data-testid="deal360-doc-role">
          {DOC_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
        </select>
        <label className="btn-primary text-xs cursor-pointer" data-testid="deal360-doc-upload">
          <FileText className="w-3.5 h-3.5" /> {uploading ? "Téléversement…" : "Téléverser"}
          <input type="file" className="hidden" disabled={uploading} onChange={(e) => upload(e.target.files?.[0])} />
        </label>
        <button onClick={openLinking} className="btn-ghost text-xs" data-testid="deal360-doc-link-existing">
          Lier un document existant
        </button>
      </div>
      {linking && (
        <div className="noam-card p-3 space-y-1" data-testid="deal360-doc-candidates">
          {candidates.length === 0 && <div className="text-[10px] text-zinc-600 p-1">Aucun autre document disponible dans le Drive.</div>}
          {candidates.map((d) => (
            <button key={d.id} onClick={() => linkExisting(d)}
              className="w-full text-left p-2 rounded bg-white/[0.03] hover:bg-white/[0.07] text-xs flex items-center gap-2 transition-colors"
              data-testid={`deal360-doc-candidate-${d.id}`}>
              <FileText className="w-3 h-3 text-zinc-500 shrink-0" />
              <span className="text-zinc-200 truncate flex-1">{d.name}</span>
              <span className="text-[10px] text-zinc-600">{d.module_source || ""}</span>
            </button>
          ))}
        </div>
      )}
      {docs.length === 0 && !linking ? (
        <EmptyState testid="deal360-docs-empty" text="Aucun document lié à ce deal. Téléversez ou liez un document existant." />
      ) : docs.length > 0 && (
        <div className="noam-card divide-y divide-white/5" data-testid="deal360-docs-list">
          {docs.map((d) => (
            <div key={d.id} className="p-3 flex items-center gap-3 text-xs">
              <FileText className="w-3.5 h-3.5 text-[#378ADD] shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-zinc-200 truncate">{d.name}</div>
                <div className="text-[10px] text-zinc-500">
                  {fmtDate(d.uploaded_at)} · {d.size_bytes ? `${Math.round(d.size_bytes / 1024)} Ko` : "—"}
                  {d.uploaded_by && ` · ${d.uploaded_by}`}
                  {d.module_source && ` · via ${d.module_source}`}
                </div>
              </div>
              {d.doc_role && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-[#EFD127]/40 text-[#EFD127] shrink-0"
                  data-testid={`deal360-doc-rolebadge-${d.id}`}>{ROLE_LABELS[d.doc_role] || d.doc_role}</span>
              )}
              {(d.tags || []).slice(0, 2).map((t) => (
                <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-full border border-white/10 text-zinc-500 shrink-0">{t}</span>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IntelligenceTab({ intel }) {
  const score = intel.score;
  const hasAny = score || intel.market_analysis || intel.metacognition || intel.ai_reading || intel.buyers;
  if (!hasAny) return <EmptyState testid="deal360-intel-empty" text="Aucune analyse IA disponible pour ce deal. Lancez le scoring depuis la page Deals." />;
  const dims = score?.dimensions || score?.dims || null;
  return (
    <div className="space-y-4">
      {score?.status === "done" && (
        <div className="noam-card p-4" data-testid="deal360-intel-score">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Score de réalisabilité — {score.global}/100</div>
          {score.verdict && <p className="text-xs text-zinc-300 mb-3">{score.verdict}</p>}
          {dims && (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
              {Object.entries(dims).map(([k, v]) => {
                const val = typeof v === "object" ? v.score ?? v.value : v;
                return (
                  <div key={k} className="p-2 rounded bg-white/[0.03] text-xs">
                    <div className="text-zinc-500 capitalize">{k}</div>
                    <div className="font-mono font-bold mt-0.5" style={{ color: scoreColor(val) }}>{val ?? "—"}</div>
                  </div>
                );
              })}
            </div>
          )}
          {(score.forces || []).length > 0 && (
            <div className="mt-3 text-xs"><span className="text-[#1D9E75]">Forces :</span> <span className="text-zinc-400">{score.forces.join(" · ")}</span></div>
          )}
          {(score.faiblesses || []).length > 0 && (
            <div className="mt-1 text-xs"><span className="text-[#E24B4A]">Faiblesses :</span> <span className="text-zinc-400">{score.faiblesses.join(" · ")}</span></div>
          )}
        </div>
      )}
      {(intel.score_history || []).length > 1 && (
        <div className="noam-card p-4 text-xs" data-testid="deal360-intel-history">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Historique du score</div>
          <div className="flex items-center gap-2 flex-wrap">
            {intel.score_history.map((h, i) => (
              <span key={i} className="px-2 py-1 rounded bg-white/[0.03] font-mono" style={{ color: scoreColor(h.global) }}>
                {h.global} <span className="text-zinc-600 text-[9px]">{fmtDate(h.at)}</span>
              </span>
            ))}
          </div>
        </div>
      )}
      {intel.market_analysis && (
        <div className="noam-card p-4 text-xs" data-testid="deal360-intel-market">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Analyse de marché (mandat)</div>
          <p className="text-zinc-400 whitespace-pre-wrap line-clamp-[12]">
            {typeof intel.market_analysis === "string" ? intel.market_analysis : intel.market_analysis.summary || intel.market_analysis.text || JSON.stringify(intel.market_analysis).slice(0, 800)}
          </p>
        </div>
      )}
      {intel.buyers && (
        <div className="noam-card p-4 text-xs" data-testid="deal360-intel-buyers">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Acheteurs / contreparties suggérés</div>
          <p className="text-zinc-400 whitespace-pre-wrap line-clamp-[10]">
            {typeof intel.buyers.profiles === "string" ? intel.buyers.profiles : JSON.stringify(intel.buyers.profiles || intel.buyers.annuaire_buyers).slice(0, 800)}
          </p>
        </div>
      )}
    </div>
  );
}

const TABS = [
  { key: "overview", label: "Overview", icon: Layers },
  { key: "asset", label: "Asset", icon: Building2 },
  { key: "parties", label: "Parties", icon: Users },
  { key: "execution", label: "Exécution & Closing", icon: ClipboardCheck },
  { key: "timeline", label: "Timeline", icon: Clock },
  { key: "negotiation", label: "Négociation", icon: Scale },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "decisions", label: "Décisions", icon: Sparkles },
  { key: "intelligence", label: "Intelligence", icon: Brain },
];

function JourneyPanel({ dealId, onGoTab }) {
  const [j, setJ] = useState(null);
  useEffect(() => {
    api.get(`/workspace/deals/${dealId}/journey`).then((r) => setJ(r.data)).catch(() => setJ(null));
  }, [dealId]);
  if (!j) return null;
  const KIND = { BLOCKER: ["#E24B4A", "BLOCKED"], RECOMMENDED: ["#EFD127", "RECOMMENDED"],
    MISSING_EVIDENCE: ["#EF9F27", "MISSING EVIDENCE"], READY: ["#1D9E75", "READY"] };
  const [color, badge] = KIND[j.next_best_action?.kind] || KIND.READY;
  return (
    <div className="noam-card p-4 space-y-2" data-testid="deal-journey-panel" style={{ borderLeft: `3px solid ${color}` }}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Vous êtes ici</div>
        <span className="text-[9px] px-2 py-0.5 rounded-full border font-semibold" style={{ color, borderColor: color }}>{badge}</span>
      </div>
      <div className="text-sm text-zinc-200 font-medium" data-testid="journey-you-are-here">{j.you_are_here?.label}</div>
      <div className="text-xs" style={{ color }} data-testid="journey-next-action">
        Prochaine action : {j.next_best_action?.action}
        {j.owner && <span className="text-zinc-500"> · owner : {j.owner}</span>}
        {j.deadline && <span className="text-zinc-500"> · échéance : {String(j.deadline).slice(0, 10)}</span>}
      </div>
      {(j.what_is_missing || []).length > 0 && (
        <div className="text-[11px] text-zinc-400" data-testid="journey-missing">
          {j.what_is_missing.map((m, i) => (
            <div key={i}>◦ <span className="text-amber-400">{m.what}</span> — <span className="text-zinc-500">{m.why}</span></div>
          ))}
        </div>
      )}
      {(j.blockers || []).length > 0 && (
        <div className="text-[11px] text-red-400" data-testid="journey-blockers">
          {j.blockers.map((b, i) => <div key={i}>⛔ {b.title} — <span className="text-zinc-500">{b.consequence}</span></div>)}
        </div>
      )}
      {(j.evidence_required || []).length > 0 && (
        <div className="text-[10px] text-zinc-500">Preuve attendue : {j.evidence_required.join(" · ")}</div>
      )}
      <div className="text-[9px] text-zinc-600">{j.principle}</div>
    </div>
  );
}

export default function Deal360() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("overview");
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get(`/workspace/deals/${id}/360`)
      .then((r) => setData(r.data))
      .catch((e) => { setError(fmtError(e)); notify.error(fmtError(e)); });

  useEffect(() => {
    setData(null); setError(null);
    load();
    // eslint-disable-next-line
  }, [id]);

  const transition = async (to) => {
    setBusy(true);
    try {
      const reason = window.prompt(`Raison du passage vers ${PHASE_LABELS[to] || to} (optionnel) :`) || undefined;
      const r = await api.post(`/workspace/deals/${id}/phase`, { to, reason });
      if (r.data.already) notify.info("Phase déjà à jour.");
      else notify.success(`Phase : ${r.data.from} → ${to}`);
      await load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det?.code === "BLOCKING_OBLIGATIONS") {
        notify.error(`${det.message} (${(det.blockers || []).map((b) => b.title).join(" · ").slice(0, 160)})`);
        setTab("execution");
      } else if (det?.code === "UNCLOSED_DECISIONS") {
        notify.error(`${det.message} (${(det.decisions || []).map((b) => b.title).join(" · ").slice(0, 160)})`);
        setTab("decisions");
      } else notify.error(fmtError(e));
    }
    setBusy(false);
  };

  if (error) {
    return (
      <div className="py-20 text-center space-y-3" data-testid="deal360-error">
        <div className="text-sm text-zinc-400">{error}</div>
        <Link to="/app/deals" className="btn-ghost text-xs inline-flex"><ArrowLeft className="w-3.5 h-3.5" /> Retour aux deals</Link>
      </div>
    );
  }
  if (!data) return <div className="flex items-center justify-center py-20 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const { identity, state, intelligence } = data;
  const d = identity.deal;

  return (
    <div className="space-y-5" data-testid="deal360-root">
      <JourneyPanel dealId={id} onGoTab={setTab} />
      <div className="noam-card p-5 space-y-4" data-testid="deal-360-header">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <Link to="/app/deals" className="text-[10px] text-zinc-500 hover:text-zinc-300 flex items-center gap-1 mb-1.5 w-fit" data-testid="deal360-back">
              <ArrowLeft className="w-3 h-3" /> Registre des deals
            </Link>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="noam-display text-xl font-bold tracking-tight">{d.title}</h1>
              <span className="text-[10px] px-2 py-0.5 rounded-full border border-[#378ADD]/40 text-[#378ADD] bg-[#378ADD]/10">{identity.asset_label}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${identity.provenance.type === "courtage" ? "border-[#EFD127]/40 text-[#EFD127] bg-[#EFD127]/10" : identity.provenance.type === "origination" ? "border-[#1D9E75]/40 text-[#1D9E75] bg-[#1D9E75]/10" : "border-white/10 text-zinc-400"}`}
                data-testid="deal360-provenance-badge">
                {identity.provenance.type === "courtage" ? "Mandat Courtage" : identity.provenance.type === "origination" ? "Chaîne Origination" : "Création directe"}
              </span>
            </div>
            <div className="text-lg font-semibold text-zinc-200 mt-1">{fmtCHF(d.valeur_chf)}</div>
          </div>
          <ScoreDonut score={intelligence.score} />
        </div>
        <PhaseStepper phase={state.phase} phases={state.phases} statut={state.statut}
          history={state.phase_history} terminal={state.terminal}
          onTransition={transition} busy={busy} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-8 space-y-4">
          <div className="flex items-center gap-1 overflow-x-auto pb-1">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 whitespace-nowrap transition-colors ${tab === t.key ? "bg-white/[0.08] text-white" : "text-zinc-500 hover:text-zinc-300"}`}
                data-testid={`deal-360-tab-${t.key}`}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>
          {tab === "overview" && <OverviewTab data={data} />}
          {tab === "asset" && <AssetTab identity={identity} />}
          {tab === "parties" && <PartiesTab identity={identity} dealId={id} onReload={load} />}
          {tab === "execution" && (
            <div className="noam-card p-4" data-testid="deal360-execution-tab">
              <ObligationsPanel system="deal" refType="deal" refId={id} refLabel={d.title}
                title="Closing checklist vivante — ce qui doit arriver, qui, avant quand" checklist />
              <div className="mt-3 text-right"><ExportButton system="deal" label="Exporter Deal System (intégral)" /></div>
            </div>
          )}
          {tab === "timeline" && <TimelineTab timeline={data.timeline} />}
          {tab === "negotiation" && <NegotiationTab state={state} dealId={id} onReload={load} />}
          {tab === "documents" && <DocumentsTab state={state} dealId={id} onReload={load} />}
          {tab === "decisions" && <DealDecisions dealId={id} onReload={load} />}
          {tab === "intelligence" && <IntelligenceTab intel={intelligence} />}
        </div>
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-4">
            <Pulse intel={intelligence} />
          </div>
        </div>
      </div>
    </div>
  );
}
